import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import OrderTable from "../../components/Table/OrderTable";
import { IoSearchSharp } from "react-icons/io5";
import Card from "../../components/Table/Card";
import { useSelector } from "react-redux";
import { complaintOrders } from "../../store";
import { useThunk } from "../../hooks/useThunk";
import { orderStatus } from "../../constants/commonConstants";
import RecordNotFound from "../../components/Table/RecordNotFound";
import showToast from "../../utils/toastNotifications";
import Pagination from "../../components/Table/pagination";
import FetchingError from "../../components/Table/FetchingError";

const RaiseComplaint = () => {

  const navigate = useNavigate();
  const { orders: raiseComplaintOrders, pagination: getPagination } = useSelector((state) => state.createOrder?.complaintOrders);

  // Thunks
  const [
    doUserComplaintOrdersFetch,
    isUserComplaintOrdersFetching,
    userComplaintOrdersError,
  ] = useThunk(complaintOrders);

  // State variables
  const itemsPerPage = 6;
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState(
    searchParams.get("search") || ""
  );
  const [currentPage, setCurrentPage] = useState(
    Number(searchParams.get("page")) || 1
  );
  const [isSearchTriggered, setIsSearchTriggered] = useState(false);

  useEffect(() => {
    const params = {
      page: currentPage,
      search: searchTerm,
    };
    setSearchParams(params);
    setCurrentPage(currentPage);
    setSearchTerm(searchTerm);
  }, [setSearchParams, currentPage, searchTerm]);

  useEffect(() => {
    // Only fetch orders if not triggered by a search action
    if (!isSearchTriggered) {
      fetchUserComplaintOrders();
    } else {
      setIsSearchTriggered(false);
    }
  }, [currentPage]);

  const fetchUserComplaintOrders = async (term = searchTerm, page = currentPage) => {
    const offset = (page - 1) * itemsPerPage;
    const limit = itemsPerPage;
    const result = await doUserComplaintOrdersFetch({
      offset,
      limit,
      searchTerm: term,
    });
    if (!result.success) {
      console.log(result?.error?.message || "Error fetching user orders");
      showToast("error", result?.error?.message);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Function to handle navigation to the raise complaint form
  const handleNavigateToOrder = (customId, threadId, objectId) => {
    const url = `/raise-complaint-forms?order_Id=${customId}&collection_Id=${objectId}`;
    navigate(url);
  };

  const handleViewOrder = (roomId, threadId) => {
    navigate(`/thread?roomIdParam=${roomId}&threadIdParam=${threadId}`);
  };

  const searchAndPressEnter = (e) => {
    if (e.key === "Enter") {
      setIsSearchTriggered(true);
      setCurrentPage(1);
      fetchUserComplaintOrders(searchTerm, 1);
    }
  };

  const handleInputChange = (e) => {
    setIsSearchTriggered(true);
    const term = e.target.value;
    setSearchTerm(term);

    // If the search term is cleared, fetch all orders
    if (term.trim() === "") {
      setIsSearchTriggered(false);
      setCurrentPage(1);
      fetchUserComplaintOrders(term, 1);
    }
  };

  const clearFilters = () => {
    setIsSearchTriggered(false);
    setSearchTerm("");
    setCurrentPage(1); 
    setSearchParams({ page: 1, search: "" }); 
    fetchUserComplaintOrders("", 1);
  };

  const onRetry = () => {
    clearFilters();
  };

  return (
    <div className="flex flex-col min-w-[340px] flex-grow max-h-screen max-sm:absolute max-sm:bottom-0 max-sm:top-0 max-sm:left-0 max-sm:right-0 overflow-x-hidden">

      <div className="text-primary font-roboto font-semibold text-[24px] pb-4 ml-[24px] lg:mt-[24px] mt-[104px]">
        <h2>Completed Orders</h2>
      </div>

      <div className="flex flex-col flex-grow overflow-y-auto">
        <div className="flex flex-col relative flex-grow bg-white font-inter mt-[32px] ml-[24px]  mr-[24px] lg:mx-[48px] mb-[48px] border border-medium-gray rounded-lg py-8 px-[16px] lg:px-[px] xl:px-[px] md:py-10 max-w-full md:max-w-[2200px] w-auto mx-auto">
          <div className="flex flex-row md:mb-0 lg:mb-0">
            <div className="flex flex-row sm:mb-4 gap-y-2 gap-x-0 lg:text-level-3 md:text-level-4 sm:text-level-5 text-gray leading-[19.36px] h-10 font-normal max-460-br:w-full">
              <div className="flex w-full items-center border border-medium-gray rounded-lg px-2 py-2 bg-white focus:outline-none ">
                <IoSearchSharp className="w-[34px] h-[40px] text-gray rounded-[3px] py-[3px] px-[5px]" />

                <input
                  type="text"
                  placeholder="Search Orders by Order ID"
                  className="ml-1 w-full text-gray font-inter text-level-4 focus:outline-none"
                  value={searchTerm}
                  onChange={handleInputChange}
                  onKeyDown={searchAndPressEnter}
                />
              </div>
            </div>
          </div>

          {/* Render Table on Large Screens */}
          <div className="hidden xl:block">
            <div className="flex flex-col flex-grow mt-20 460-br:mt-12 sm:mt-0">
              <OrderTable
                orders={raiseComplaintOrders} // Use the filtered data here
                onViewOrder={handleNavigateToOrder}
                ordersPerPage={itemsPerPage}
                orderStatusFilter="Completed"
                columnNames={{
                  orderId: "Order ID",
                  subject: "Subject",
                  price: "Price",
                  orderStatus: "Order Status",
                  created: "Created",
                  dueDate: "Completed Date",
                  actions: "Actions",
                }}
                buttonText="Raise Complaint"
                secondaryButtonText="View Order"
                onSecondaryViewOrder={handleViewOrder}
                button
                onClick="raise-complaint-forms"
                currentPage={currentPage}
                pagination={getPagination}
                onPageChange={handlePageChange}
                loading={isUserComplaintOrdersFetching}
                tableName={orderStatus.COMPLAINT_RAISED}
                onRetry={onRetry}
                error={userComplaintOrdersError}
              />
            </div>
          </div>

          {/* Render Cards on Small Screens */}
          <div className="xl:hidden">
            <div className="flex flex-col flex-grow  mt-4 sm:mt-0 460-br:mt-12">
              <>
                {
                   // display order not found message if there are no orders user search
                   ((searchTerm && raiseComplaintOrders.length === 0) || userComplaintOrdersError) ? (
                    <FetchingError 
                      error={userComplaintOrdersError} 
                      isEmptyRecords={raiseComplaintOrders.length === 0} 
                      onRetry={onRetry}
                    />
                  ) : (
                    <Card
                      buttonType="Raise Complaint"
                      orderStatusFilter="Completed"
                      currentOrders={raiseComplaintOrders}
                      onViewOrder={handleNavigateToOrder}
                      secondaryButtonText="View Order"
                      onSecondaryViewOrder={handleViewOrder}
                      currentPage={currentPage}
                      pagination={getPagination}
                      onPageChange={handlePageChange}
                      ordersPerPage={itemsPerPage}
                      className="absolute -bottom-12 self-center bg-white "
                      loading={isUserComplaintOrdersFetching}
                      tableName={orderStatus.COMPLAINT_RAISED}
                    />
                  )
                }
              </>
            </div>
          </div>

          {/* Pagination Controls */}
          <Pagination
            totalItems={getPagination.totalDocuments || 0}
            itemsPerPage={itemsPerPage}
            currentPage={currentPage}
            onPageChange={handlePageChange}
            className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 bg-white "
          />
        </div>
      </div>
    </div>
  );
};

export default RaiseComplaint;
